import wretch from 'wretch';

import { configureErrorCatchers } from '@cx-modules/errors';
import { getEnvContextualizedSportsCatalogueEndpoint } from '@cx-modules/utils';

import { type LoadSubeventHeaderArgs } from '../types';

// The @cx-modules/client `LoadServerCxModule*` types were removed/reshaped by the loadUmdBundle
// cleanup (d68cf3981), so we inline the shapes here rather than importing them from
// @cx-modules/client. Two reasons: (1) `LoadServerCxModuleRequestParams<T>` no longer exists —
// `dataApiEndpoint` is passed to the fetcher separately, so the request params are the loader args
// minus that field; (2) importing a *type* from @cx-modules/client pulls its source into this
// module's bundled .d.ts build (dts-bundle-generator walks the client export graph and its
// `queryClientFactory` source), which is unnecessary here. `LoadServerCxModuleOptions` mirrors
// @cx-modules/client's shape and stays assignable to @cx-modules/errors' configureErrorCatchers.
export type SubeventHeaderRequestParams = Omit<LoadSubeventHeaderArgs, 'dataApiEndpoint'>;

type CxModuleLoadingOptions = {
  cacheTtlInMs?: number;
};

type LoadServerCxModuleOptions = {
  dependenciesMap?: Record<string, unknown>;
  loadingOptions?: CxModuleLoadingOptions;
  logger?: {
    debug: (...args: unknown[]) => void;
    error: (...args: unknown[]) => void;
    info: (...args: unknown[]) => void;
    log: (...args: unknown[]) => void;
    warn: (...args: unknown[]) => void;
  };
  nocache?: boolean;
};

export type SubeventHeaderResponseData = {
  breadcrumbs: Breadcrumb[];
  categoryGroupId: number;
  eventName: string;
  inRunning: boolean;
  relatedSubevents: RelatedSubevent[];
  subeventEndTime: string;
  subeventName: string;
  subeventStartTime: string;
  subeventUrl: string;
};

export type Breadcrumb = {
  name: string;
  url: string;
};

export type RelatedSubevent = {
  name: string;
  startTime: string;
  url: string;
};

export const fetchSubeventHeaderData = async (
  dataApiEndpoint = getEnvContextualizedSportsCatalogueEndpoint(),
  params: SubeventHeaderRequestParams,
  options?: LoadServerCxModuleOptions,
): Promise<SubeventHeaderResponseData | null> => {
  const { repub, subeventId } = params;
  const url = new URL(`${dataApiEndpoint}/v1/subevent-header/${subeventId}`);

  const client = configureErrorCatchers(
    wretch(url.toString(), {
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        repub,
        ...(options?.nocache && { 'Cache-Control': 'no-cache' }),
      },
    }),
    options,
  );

  return client.get().json<SubeventHeaderResponseData>();
};

export const getSubeventHeaderCacheKey = ({ repub, subeventId }: SubeventHeaderRequestParams) => [
  'subevent-header',
  repub,
  subeventId,
];
